import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { Button } from '../components/Button';
import { useExam } from '../context/ExamContext';
import {
  createPublicDemoCandidate,
  DEMO_CANDIDATE,
  PUBLIC_DEMO_CODE,
  PublicDemoDetails,
  PublicDemoErrors,
  validatePublicDemoDetails
} from '../services/publicDemoSession';

const fields: { key: keyof PublicDemoDetails; label: string }[] = [
  { key: 'name', label: 'Full name' },
  { key: 'email', label: 'Email' },
  { key: 'examCode', label: 'Exam code' }
];

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useExam();
  const [form, setForm] = useState<PublicDemoDetails>({ name: '', email: '', examCode: PUBLIC_DEMO_CODE });
  const [errors, setErrors] = useState<PublicDemoErrors>({});

  function validate() {
    const next = validatePublicDemoDetails(form);
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!validate()) return;
    localStorage.removeItem('mockExamToken');
    login(createPublicDemoCandidate(form));
    navigate('/instructions');
  }

  return (
    <section className="mx-auto max-w-xl px-4 py-12">
      <p className="text-sm font-semibold uppercase text-neon-blue">Prototype Demo</p>
      <h1 className="mt-2 text-3xl font-bold">Explore ExamSphere AI</h1>
      <p className="mt-2 text-slate-600 dark:text-slate-300">
        Enter your details to explore the dual-camera AI proctoring experience. No registration or password is required.
      </p>
      <form noValidate onSubmit={submit} className="mt-8 space-y-4 rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {fields.map(({ key, label }) => (
          <label key={key} className="block">
            <span className="text-sm font-semibold">{label}</span>
            <input
              type={key === 'email' ? 'email' : 'text'}
              className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-950"
              value={form[key]}
              onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
              aria-invalid={Boolean(errors[key])}
              aria-describedby={key === 'examCode' ? 'examCode-help examCode-error' : `${key}-error`}
              autoComplete={key === 'name' ? 'name' : key === 'email' ? 'email' : 'off'}
              required
            />
            {key === 'examCode' ? <span id="examCode-help" className="mt-1 block text-sm text-slate-500 dark:text-slate-400">Public prototype code — anyone may join.</span> : null}
            {errors[key] ? <span id={`${key}-error`} className="mt-1 block text-sm text-red-400">{errors[key]}</span> : null}
          </label>
        ))}
        <Button type="button" variant="secondary" className="w-full" onClick={() => { setForm({ ...DEMO_CANDIDATE }); setErrors({}); }}>
          Use Demo Candidate
        </Button>
        <Button type="submit" icon={<LogIn className="h-4 w-4" />} className="w-full">
          Continue to Exam Setup
        </Button>
      </form>
    </section>
  );
}
